import { Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import type { ProjectTimeline } from '@/types/firestore';
import { PROJECT_TIMELINE_LABELS } from '@/types/firestore';
import { cn } from '@/lib/utils';

interface TimelineSelectorProps {
  value?: ProjectTimeline;
  onChange: (timeline: ProjectTimeline) => void;
  error?: string;
  required?: boolean;
}

const TIMELINE_OPTIONS: { value: ProjectTimeline; label: string; description: string }[] = [
  {
    value: 'asap',
    label: 'ASAP',
    description: 'Ready to start immediately',
  },
  {
    value: '30_days',
    label: '30 Days',
    description: 'Within the next month',
  },
  {
    value: '60_days',
    label: '60 Days',
    description: 'Within 2 months',
  },
  {
    value: '90_days',
    label: '90 Days',
    description: 'Within 3 months',
  },
  {
    value: 'exploring',
    label: 'Exploring',
    description: 'Just looking at options',
  },
];

export function TimelineSelector({ value, onChange, error, required = true }: TimelineSelectorProps) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <Label>When do you want to start?</Label>
        {!required && <span className="text-muted-foreground text-xs">(Optional)</span>}
      </div>

      <div
        className="grid grid-cols-2 gap-2 sm:grid-cols-3"
        role="radiogroup"
        aria-label="Project timeline selection"
      >
        {TIMELINE_OPTIONS.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            aria-label={`${option.label}: ${option.description}`}
            onClick={() => onChange(option.value)}
            className={cn(
              'flex flex-col items-center p-3 rounded-lg border-2 transition-all text-center',
              value === option.value
                ? 'border-primary bg-primary/5 ring-1 ring-primary/20'
                : 'border-muted hover:border-muted-foreground/30 hover:bg-muted/50'
            )}
          >
            <span className={cn(
              'font-semibold text-sm',
              value === option.value ? 'text-primary' : 'text-foreground'
            )}>
              {option.label}
            </span>
            <span className="text-xs text-muted-foreground mt-0.5">
              {option.description}
            </span>
          </button>
        ))}
      </div>

      {error && <p className="text-xs text-destructive" role="alert">{error}</p>}
    </div>
  );
}
